import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { ChevronsLeft, ChevronsRight } from "react-feather";

import { green } from "@mui/material/colors";

import {
  Box,
  Chip,
  Drawer as MuiDrawer,
  ListItemButton,
  ListItemText,
} from "@mui/material";

import { ReactComponent as Logo } from "../../vendor/logo.svg";
import Footer from "./SidebarFooter";
import SidebarNav from "./SidebarNav";
import SidebarContext, { useSidebarFade } from "./SidebarContext";

// The icon bar the sidebar shrinks to when it autohides.
export const RAIL_WIDTH = 64;

// A pointer crossing the icon bar shouldn't open it, and one that slips
// off for a moment shouldn't close it.
const OPEN_DELAY = 120;
const CLOSE_DELAY = 200;

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
  }
`;

// The logo sits at the left, centred in the icon bar, so it stays put
// as the sidebar opens.
const Brand = styled(ListItemButton)`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(4)};
  padding-right: ${(props) => props.theme.spacing(6)};
  justify-content: flex-start;
  cursor: pointer;
  flex-grow: 0;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${(props) => props.theme.sidebar.header.background};
  }
`;

const BrandIcon = styled(Logo)`
  margin-right: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.sidebar.header.brand.color};
  fill: ${(props) => props.theme.sidebar.header.brand.color};
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const BrandChip = styled(Chip)`
  background-color: ${green[700]};
  border-radius: 5px;
  color: ${(props) => props.theme.palette.common.white};
  font-size: 55%;
  height: 18px;
  margin-left: 2px;
  margin-top: -16px;
  padding: 3px 0;

  span {
    padding-left: ${(props) => props.theme.spacing(1.375)};
    padding-right: ${(props) => props.theme.spacing(1.375)};
  }
`;

// Pins the sidebar open, or lets it shrink back to the icon bar. Its icon
// lines up with the nav's, so it shows on the bar too (for touch screens,
// which can't hover).
const RailToggle = styled(ListItemButton)`
  flex-grow: 0;
  padding: ${(props) => props.theme.spacing(3)}
    ${(props) => props.theme.spacing(7)} ${(props) => props.theme.spacing(3)}
    ${(props) => props.theme.spacing(5.5)};
  color: ${(props) => props.theme.sidebar.color};
  background-color: ${(props) => props.theme.sidebar.background};
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-right: 1px solid rgba(0, 0, 0, 0.12);

  svg {
    color: ${(props) => props.theme.sidebar.color};
    width: 20px;
    height: 20px;
    opacity: 0.5;
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  .MuiListItemText-root {
    margin: 0;
  }

  .MuiListItemText-primary {
    color: ${(props) => props.theme.sidebar.color};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
    white-space: nowrap;
  }
`;

function Contents({ items, showFooter, rail, onToggleRail }) {
  const fade = useSidebarFade();
  return (
    <React.Fragment>
      <Brand component={NavLink} to="/">
        <BrandIcon />{" "}
        <Box ml={1} sx={{ ...fade, whiteSpace: "nowrap" }}>
          Gulf-Pro <BrandChip label="Admin" />
        </Box>
      </Brand>
      <SidebarNav items={items} />
      {onToggleRail && (
        <RailToggle
          onClick={onToggleRail}
          aria-label={rail ? "Keep the sidebar open" : "Shrink the sidebar"}
        >
          {rail ? <ChevronsRight /> : <ChevronsLeft />}
          <ListItemText
            sx={fade}
            primary={rail ? "Keep sidebar open" : "Shrink sidebar"}
          />
        </RailToggle>
      )}
      {!!showFooter && <Footer />}
    </React.Fragment>
  );
}

// The app's sidebar. With `onToggleRail` it can autohide: while `rail` is
// set it shows as an icon bar (RAIL_WIDTH) that opens over the page while
// the pointer or keyboard focus is on it, so the page keeps its width.
// Its contents keep their full width either way and are clipped, so
// nothing reflows as it opens.
const Sidebar = ({
  items,
  showFooter = true,
  rail = false,
  onToggleRail,
  PaperProps = {},
  ...rest
}) => {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const timer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(timer.current), []);

  const hover = (next) => () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => setHovered(next),
      next ? OPEN_DELAY : CLOSE_DELAY
    );
  };

  const width = PaperProps.style?.width || 258;
  const collapsed = rail && !hovered && !focused;
  const context = React.useMemo(() => ({ collapsed }), [collapsed]);
  const paper = onToggleRail
    ? {
        ...PaperProps,
        onMouseEnter: hover(true),
        onMouseLeave: hover(false),
        onFocus: () => setFocused(true),
        onBlur: (event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setFocused(false);
          }
        },
        style: {
          ...PaperProps.style,
          ...(rail && {
            width: collapsed ? RAIL_WIDTH : width,
            overflowX: "hidden",
            transition: "width 200ms ease, box-shadow 200ms ease",
            boxShadow: collapsed ? "none" : "4px 0 16px rgba(0, 0, 0, 0.24)",
          }),
        },
        // The bar's edge, which the clipped nav's own border can't draw.
        sx: rail
          ? {
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: "1px",
                bgcolor: "rgba(0, 0, 0, 0.12)",
                pointerEvents: "none",
                zIndex: 1,
              },
            }
          : undefined,
      }
    : PaperProps;

  return (
    <SidebarContext.Provider value={context}>
      <Drawer variant="permanent" {...rest} PaperProps={paper}>
        <Box
          sx={{
            width,
            minWidth: width,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Contents
            items={items}
            showFooter={showFooter}
            rail={rail}
            onToggleRail={onToggleRail}
          />
        </Box>
      </Drawer>
    </SidebarContext.Provider>
  );
};

export default Sidebar;
